import { Image } from '@chakra-ui/react'
import { capitalize } from 'lodash'
import React from 'react'
import {
  RiCheckFill,
  RiCloseFill,
  RiLoader2Fill,
  RiLoopRightLine,
  RiTimerLine,
} from 'react-icons/ri'
import { env } from 'env.client'
import {
  BatchListItemRollupStatus,
  QuarantinedTransactionQuarantineQuarantineType as QuarantinedType,
  TransactionListItemExitStatus,
  TransactionListItemStatus,
} from 'generated/reactQueryClient'

export const MAX_PAGE_LENGTH = 100 as const
export const PAGE_LENGTH = 25 as const
export const SEARCH_BAR_ITEMS_LIMIT = 5 as const
export const FIRST_PAGE = 1 as const

export const MAX_FETCH_LIMIT = 100

// If address is not a contract.
// https://ethereum.stackexchange.com/questions/28521/how-to-detect-if-an-address-is-a-contract.
export const STANDARD_ADDRESS_CODE = '0x'

export const EIP7702_DELEGATION_PREFIX = '0xef0100'

export const EIP7702_TRANSACTION_TYPE = '4'

export const TXN_STATUS_VARIANTS = {
  [TransactionListItemStatus.successful]: 'success',
  [TransactionListItemStatus.failed]: 'failed',
  [TransactionListItemStatus.pending]: 'critical',
  [TransactionListItemStatus.replaced]: 'information',
} as const

export const EXIT_TXN_STATUS_VARIANTS = {
  [TransactionListItemExitStatus.waiting]: 'critical',
  [TransactionListItemExitStatus.relayed]: 'neutral',
} as const

export const ROLLUP_STATUS_VARIANTS = {
  [BatchListItemRollupStatus.created]: 'neutral',
  [BatchListItemRollupStatus.pending]: 'information',
  [BatchListItemRollupStatus.published]: 'success',
  [BatchListItemRollupStatus.waiting_for_proof]: 'critical',
} as const

export const USER_OPERATION_STATUS_VARIANTS = {
  success: 'success',
  submitting: 'critical',
  failed: 'failed',
  intiated: 'information',
} as const

export const TXN_STATUS_ICONS = {
  [TransactionListItemStatus.successful]: <RiCheckFill size={16} />,
  [TransactionListItemStatus.failed]: <RiCloseFill size={16} />,
  [TransactionListItemStatus.pending]: <RiTimerLine size={16} />,
  [TransactionListItemStatus.replaced]: <RiLoopRightLine size={16} />,
} as const

export const EXIT_TXN_STATUS_ICONS = {
  [TransactionListItemExitStatus.waiting]: <RiTimerLine size={16} />,
  [TransactionListItemExitStatus.relayed]: <RiLoader2Fill size={16} />,
} as const

export const ROLLUP_STATUS_ICONS = {
  [BatchListItemRollupStatus.created]: <Image alt="Created" src="/assets/created.svg" />,
  [BatchListItemRollupStatus.pending]: <Image alt="Pending" src="/assets/pending.svg" />,
  [BatchListItemRollupStatus.published]: <RiCheckFill size={16} />,
  [BatchListItemRollupStatus.waiting_for_proof]: <RiTimerLine size={16} />,
} as const

export const USER_OPERATION_STATUS_ICONS = {
  success: <RiCheckFill size={16} />,
  failed: <RiCloseFill size={16} />,
  submitting: <RiTimerLine size={16} />,
  initiated: <RiLoader2Fill size={16} />,
} as const

export const ROLLUP_STATUS_LABELS = {
  [BatchListItemRollupStatus.created]: 'Created',
  [BatchListItemRollupStatus.pending]: 'Pending',
  [BatchListItemRollupStatus.published]: 'Published',
  [BatchListItemRollupStatus.waiting_for_proof]: 'Waiting for prover',
}

export const EXIT_TXN_STATUS_LABELS = {
  [TransactionListItemExitStatus.waiting]: 'Waiting',
  [TransactionListItemExitStatus.relayed]: 'Relayed',
  [TransactionListItemExitStatus.ready_to_relay]: 'Ready to claim',
  [TransactionListItemExitStatus.in_challenge_period]: 'In claiming period',
  [TransactionListItemExitStatus.ready_to_prove]: 'Ready to claim',
}

export const USER_OPERATION_STATUS_LABELS = {
  success: 'Success',
  failed: 'Failed',
  submitting: 'Submitting',
  initiated: 'Initiated',
}

export const ERROR_MESSAGES = {
  web3ContractCode: 'Failed to load address code. Check if provided address hash is valid.',
  contractVerificationInput:
    'Verification failed. Please, check if you have uploaded the correct file(s) with the corresponding contract bytecode.',
  compilationError: 'Compilation Error',
  tooManyRequests: 'Too many requests. You have reached our rate limit. Try again in one minute.',
  invalidTransactionHash: 'Invalid transaction hash.',
  pricedToken: 'Something went wrong while fetching priced token.',
}

export const CONTRACT_INPUT_LIMIT_SIZE = 1024 * 1024 * 1.14 // Maximum input size limit in bytes (1.2MB).

export const VERIFICATION_OPTION_IDS = {
  jsonInput: 'jsonInput',
  solidityInput: 'solidityInput',
} as const

export const SOLIDITY_LANGUAGE = 'Solidity' as const

export const MINIMUM_FILES_REQUIRED_FOR_SOLIDITY_FILE_VERIFICATION = 2

export const COMPILATION_OUTPUT_SELECTION = {
  '*': {
    '*': ['abi', 'evm.bytecode', 'evm.deployedBytecode', 'metadata'],
    '': ['ast'],
  },
}

export const COMPILE_ERROR_SEVERITIES = {
  error: 'error',
} as const

export const DETAIL_CARD_PADDING_X = 10
export const DETAIL_CARD_PADDING_Y = 8

export const NOT_APPLICABLE = 'N/A' as const

export const DECIMALS = 18

export const ERROR_CODES = {
  tooManyRequests: 429,
}

export const COLOR_MODE = {
  LIGHT: 'light',
  DARK: 'dark',
} as const

export const QUARANTINE_TYPE = {
  [QuarantinedType.POOL]: 'L2 -> L2',
  [QuarantinedType.DEPOSIT]: 'L1 -> L2',
}

export const NON_PROXY_CONTRACT_IMPLEMENTATION_ADDRESS =
  '0x0000000000000000000000000000000000000000000000000000000000000000' as const

export const EIP_1967_IMPLEMENTATION_SLOT =
  '0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc' as const

export const PRE_EIP_1967_IMPLEMENTATION_SLOT =
  '0x7050c9e0f4ca769c69bd3a8ef740bc37934f8e2c036e5a723fd8ee048ed3f8c3' as const

export const EIP_1967_BEACON_SLOT =
  '0xa3f0ad74e5423aebfd80d3ef4346578335a9a72aeaee59ff6cb3582b35133d50' as const

export const GNOSIS_SINGLETON_SLOT = '0x0' as const

export const TUPLE_TYPE = 'tuple' as const

export const NON_EXISTING_PROOFS = ['0xdead', '0xffff'] as const

export const TEMPLATE_PROOF_HREF =
  'https://docs.zircuit.com/architecture-and-concepts/architecture/template-proofs' as const

export const TRANSACTION_PARAM = 'tx' as const

export const APP_URL =
  env.NEXT_PUBLIC_NETWORK === 'mainnet'
    ? 'https://explorer.zircuit.com'
    : `https://explorer.${env.NEXT_PUBLIC_NETWORK}.zircuit.com`

export const LOG_DECODE_OPTIONS = {
  HEX: 'Hex',
  DEC: 'Dec',
} as const

export const FULL_ZIRCUIT_NETWORK_NAME = `Zircuit ${capitalize(env.NEXT_PUBLIC_NETWORK)}`

export const TIMESTAMP_COLUMN_FORMAT = {
  AGE: 'Age',
  DATE_TIME: 'Date Time (UTC)',
} as const

export const BATCH_OPTIONS = {
  batchSize: 100,
  wait: 1_000,
} as const

export const TRUNCATED_MOBILE_TEXT_WIDTH = { base: 110, sm: 212, md: 375 } as const

export const FILE_TYPE_EXTENSIONS = {
  solidity: '.sol',
  json: '.json',
} as const

export const PRE_BATCHES_BLOCKS_NUMBER = env.NEXT_PUBLIC_PRE_BATCHES_HARDFORK_BLOCK_NUMBER ?? null

export const HARDFORK_STATUS = {
  preHardfork: 'pre-hardfork',
  postHardfork: 'post-hardfork',
} as const
